import Box from '@mui/material/Box'
import Container from '@mui/material/Container/Container'
import CssBaseline from '@mui/material/CssBaseline/CssBaseline'
import React, { useEffect } from 'react'
import { AnalyticsContext } from '@/app/context/AnalyticsContext/AnalyticsContext'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from '@/shared/ui/ErrorFallback'
import LoginScreen from '@/pages/loginScreen/LoginScreen'
import { DIProvider } from '@/app/context/DIContext/DIProvider'
import { MainScreen } from '@/pages/mainScreen/MainScreen'
import { GetAppError } from '@/shared/lib'
import { TopMenu /*, Footer */ } from '@/widgets'
import { CacheProvider } from './context/CacheContext'
import { useApp } from './app/useApp'
import { ComponentsRenderProvider } from '@/app/context/ComponentsRenderContext/ComponentsRenderContext'
import { MobileAppContent } from '@/widgets'
import { useDetectDevice } from '@/shared/hooks'
import { NewsletterContent } from '@/pages/Settings/NewsletterContent'
import { useLocation } from 'react-router-dom'
import { newsletterCancelPath } from '@/shared/constants'
import { useAppStore } from '@/shared/store'
import {
    getFromLocalStorage,
    saveToLocalStorage,
    sessionTokenLsKeyword,
} from '@/shared/localStorage/localStorageRepository'
import { vodPagePath, vodType } from '@/shared/constants/vodConstants'
import { tempGuestToken } from '@/api/apiConstants'

function App(): JSX.Element {
    const {
        appContainerStyle,
        isUserLoggedIn,
        analyticsMethods,
        isAppReady,
        analyticsLogEvent,
    } = useApp()
    const { userUseMobileBrowser } = useDetectDevice()
    const { setIsGuest, setIsUserLoggedIn } = useAppStore()

    const { pathname, search } = useLocation()

    useEffect(() => {
        if (isUserLoggedIn) return

        const searchParams = new URLSearchParams(search)
        const productId = searchParams.get('id')
        const assetType = searchParams.get('type')?.toLowerCase()
        const isDirectVodLink =
            pathname.startsWith(vodPagePath) &&
            !!productId &&
            assetType === vodType

        if (!isDirectVodLink) return

        setIsGuest(true)
        setIsUserLoggedIn(true)

        if (!getFromLocalStorage(sessionTokenLsKeyword)) {
            saveToLocalStorage(sessionTokenLsKeyword, tempGuestToken)
        }
    }, [isUserLoggedIn, pathname, search, setIsGuest, setIsUserLoggedIn])

    return (
        <Box id="MainScreen" sx={appContainerStyle}>
            <DIProvider>
                <CacheProvider>
                    <CssBaseline />
                    {isUserLoggedIn && <TopMenu />}
                    <Container maxWidth="xl" disableGutters>
                        <AnalyticsContext.Provider
                            value={{ ...analyticsMethods }}>
                            <ErrorBoundary FallbackComponent={ErrorFallback}>
                                <GetAppError logFunction={analyticsLogEvent}>
                                    <Box className="App">
                                        {userUseMobileBrowser && (
                                            <MobileAppContent />
                                        )}

                                        {/* eslint-disable-next-line no-nested-ternary */}
                                        {!isAppReady ? null : pathname ===
                                          newsletterCancelPath ? (
                                            <NewsletterContent />
                                        ) : !isUserLoggedIn ? (
                                            <LoginScreen />
                                        ) : (
                                            <ComponentsRenderProvider>
                                                <MainScreen />
                                            </ComponentsRenderProvider>
                                        )}
                                    </Box>
                                </GetAppError>
                            </ErrorBoundary>
                        </AnalyticsContext.Provider>
                    </Container>
                    {/* TODO: Od komentować stopkę po developmentcie przez CodeSpot */}
                    {/* {isUserLoggedIn && <Footer />} */}
                </CacheProvider>
            </DIProvider>
        </Box>
    )
}

export default App
